/**
 * Method library filtering (spec US-MB-1). Pure: combined filters (type/tags/
 * group size/duration/mode/locale/text) return only matching methods.
 */
import type { Method, MethodFilter } from "./types.js";

export interface GroupRange {
  min: number;
  max: number;
}

/** Parse a group-size label ("4-30", "2-8", "10", "qualquer"/null) to a range. */
export function parseGroupSize(label: string | null): GroupRange {
  if (!label) return { min: 0, max: Infinity };
  const normalized = label.trim().toLowerCase();
  if (normalized === "qualquer" || normalized === "any" || normalized === "cualquiera") {
    return { min: 0, max: Infinity };
  }
  const range = /^(\d+)\s*[-–]\s*(\d+)$/.exec(normalized);
  if (range) return { min: Number(range[1]), max: Number(range[2]) };
  const single = /^(\d+)\+?$/.exec(normalized);
  if (single) return { min: Number(single[1]), max: normalized.endsWith("+") ? Infinity : Number(single[1]) };
  return { min: 0, max: Infinity };
}

function foldDiacritics(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function filterMethods(methods: readonly Method[], filter: MethodFilter): Method[] {
  const q = filter.query ? foldDiacritics(filter.query) : null;
  return methods.filter((m) => {
    if (filter.locale && m.locale !== filter.locale) return false;
    if (filter.mode && m.mode !== filter.mode) return false;
    if (filter.maxDurationMin != null && (m.durationMin ?? Infinity) > filter.maxDurationMin) return false;
    if (filter.tags && filter.tags.length > 0 && !filter.tags.every((tag) => m.tags.includes(tag))) return false;

    if (filter.minGroup != null || filter.maxGroup != null) {
      const g = parseGroupSize(m.groupSize);
      if (filter.minGroup != null && g.max < filter.minGroup) return false;
      if (filter.maxGroup != null && g.min > filter.maxGroup) return false;
    }

    if (q) {
      const haystack = foldDiacritics(`${m.title} ${m.bodyMd} ${m.tags.join(" ")}`);
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

/** All distinct tags across a method set, sorted — for filter chips. */
export function collectTags(methods: readonly Method[]): string[] {
  const set = new Set<string>();
  for (const m of methods) for (const tag of m.tags) set.add(tag);
  return [...set].sort();
}
